import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Zap, Leaf, Globe, Users, Target, Heart, 
  ArrowRight, CheckCircle2, Building2
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Leaf,
      title: 'Qëndrueshmëri',
      description: 'Jemi të përkushtuar ndaj një të ardhme më të gjelbër duke promovuar mobilitetin elektrik dhe energjinë e pastër.'
    },
    {
      icon: Target,
      title: 'Inovacion',
      description: 'Investojmë vazhdimisht në teknologji të reja për të ofruar eksperiencën më të mirë të karikimit.'
    },
    {
      icon: Users,
      title: 'Besueshmëri',
      description: 'Ndërtojmë marrëdhënie afatgjata me klientët tanë përmes shërbimit të besueshëm dhe transparent.'
    },
    {
      icon: Heart,
      title: 'Përkushtim',
      description: 'Çdo vendim që marrim ka në qendër nevojat e përdoruesve tanë dhe mirëqenien e komunitetit.'
    }
  ];

  const milestones = [
    { year: '2020', title: 'Themelimi', desc: 'Filloi misioni ynë për mobilitet të qëndrueshëm' },
    { year: '2021', title: 'Stacioni i Parë', desc: 'Hapet stacioni i parë në Tiranë' },
    { year: '2022', title: 'Zgjerim Kombëtar', desc: 'Rrjeti shtrihet në 5 qytete' },
    { year: '2023', title: 'Ultra-Fast', desc: 'Instalimi i karikuesve 240 kW' },
    { year: '2024', title: 'Aplikacioni', desc: 'Lançohet aplikacioni mobile' }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
                Rreth Nesh
              </span>
              <h1 
                data-testid="about-title"
                className="text-4xl md:text-6xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-6"
              >
                Energji për një<br />
                <span className="text-blue-600">Shqipëri më të Gjelbër</span>
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                AlbMove është operatori kryesor i rrjetit të karikimit për automjete elektrike në Shqipëri. 
                Misioni ynë është të bëjmë karikimin elektrik të thjeshtë, të përballueshëm dhe të aksesueshëm për të gjithë.
              </p>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-4xl font-bold text-blue-600 font-['Inter_Tight']">8+</p>
                  <p className="text-slate-500 text-sm">Qytete</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div>
                  <p className="text-4xl font-bold text-blue-600 font-['Inter_Tight']">25+</p>
                  <p className="text-slate-500 text-sm">Karikues</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div>
                  <p className="text-4xl font-bold text-blue-600 font-['Inter_Tight']">1250+</p>
                  <p className="text-slate-500 text-sm">MWh</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760201938689-a2b0f52bbcd9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMGNhciUyMGRyaXZpbmclMjBzY2VuaWMlMjByb2FkJTIwYWxiYW5pYSUyMG5hdHVyZXxlbnwwfHx8fDE3Njk3MzgyMTl8MA&ixlib=rb-4.1.0&q=85"
                alt="Berat, Albania"
                className="rounded-3xl shadow-lg"
                data-testid="about-hero-image"
              />
              <div className="absolute -bottom-4 -left-4 bg-lime-400 text-slate-900 rounded-2xl px-6 py-4">
                <p className="font-bold font-['Inter_Tight']">100%</p>
                <p className="text-sm">Energji e Pastër</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-6">
              <Globe className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 font-['Inter_Tight'] mb-6">
              Misioni Ynë
            </h2>
            <p className="text-xl text-slate-500 leading-relaxed">
              Të ndërtojmë infrastrukturën e nevojshme për të mbështetur tranzicionin e Shqipërisë drejt 
              mobilitetit elektrik, duke ofruar shërbime karikimi të besueshme, të përballueshme dhe 
              miqësore me mjedisin.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
              Vlerat Tona
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
              Çfarë Na Udhëheq
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white p-8 rounded-3xl border border-slate-200/50 text-center hover:border-blue-200 hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 font-['Inter_Tight'] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-500 leading-relaxed text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
              Historia Jonë
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
              Udhëtimi i AlbMove
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-blue-200 transform md:-translate-x-1/2" />
              
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  data-testid={`milestone-${index}`}
                  className={`relative flex items-center gap-8 mb-12 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow transform -translate-x-1/2" />
                  
                  {/* Content */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-2">
                      {milestone.year}
                    </span>
                    <h3 className="text-xl font-semibold text-slate-900 font-['Inter_Tight'] mb-1">
                      {milestone.title}
                    </h3>
                    <p className="text-slate-500">{milestone.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding text-white" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-lime-400 mb-4">
                Pse AlbMove?
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold font-['Inter_Tight'] tracking-tight mb-8">
                Partner i Besueshëm për Mobilitetin Elektrik
              </h2>
              
              <ul className="space-y-4">
                {[
                  'Rrjeti më i gjerë në Shqipëri',
                  'Teknologji e avancuar karikimi',
                  'Mbështetje 24/7 për klientët',
                  'Çmime konkurruese dhe transparente',
                  'Zgjidhje të personalizuara për biznese',
                  'Angazhim për energji të pastër'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-lime-400 flex-shrink-0" />
                    <span className="text-blue-100">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-blue-800/50 rounded-3xl p-8 md:p-12">
              <Building2 className="w-12 h-12 text-lime-400 mb-6" />
              <h3 className="text-2xl font-semibold font-['Inter_Tight'] mb-4">
                Zgjidhje për Biznese
              </h3>
              <p className="text-blue-200 leading-relaxed mb-6">
                Ofrojmë paketa të personalizuara për kompani që duan të kalojnë në flotë elektrike. 
                Nga instalimi i karikuesve deri tek menaxhimi i plotë i konsumit.
              </p>
              <Button 
                asChild
                data-testid="business-cta-button"
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full"
              >
                <Link to="/contact">
                  Kërkoni Ofertë
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 font-['Inter_Tight'] mb-6">
            Dëshironi të Bashkëpunoni?
          </h2>
          <p className="text-lg text-slate-500 mb-8 max-w-xl mx-auto">
            Jemi gjithmonë të hapur për partneritete të reja. Na kontaktoni për të diskutuar mundësitë.
          </p>
          <Button 
            asChild
            data-testid="contact-cta-button"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg"
          >
            <Link to="/contact">
              Na Kontaktoni
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
